import { useState } from 'react';
import {
  Badge,
  Button,
  Card,
  DataTable,
  EmptyState,
  KeyValue,
  Modal,
  Select,
  Skeleton,
  TextInput,
  Field,
} from '@api-hub/ui';
import { PageHeader } from '../components/Layout';
import { useResource } from '../lib/hooks';
import {
  formatCost,
  formatDateTime,
  formatMs,
  formatNumber,
  toneForRequestStatus,
} from '../lib/format';

interface RequestRow {
  id: string;
  createdAt: string;
  status: string;
  errorCode: string | null;
  httpStatus: number | null;
  operationKey: string | null;
  operationVersion: string | null;
  modelAlias: string | null;
  environment: string;
  channel: string;
  identityClass: string;
  clientApp: string | null;
  developerToken: string | null;
  latencyMs: number | null;
  retryCount: number;
  attemptCount: number;
  totalTokens: number | null;
  estimatedCostUsd: string | null;
  costStatus: string;
  cacheHit: boolean;
  streamed: boolean;
  mcpToolName: string | null;
  mcpClient: string | null;
}

interface RequestDetail {
  id: string;
  status: string;
  errorCode: string | null;
  operationKey: string | null;
  operationVersion: string | null;
  catalogVersion: number | null;
  environment: string;
  accessChannel: string;
  identityClass: string;
  latencyMs: number | null;
  ttfbMs: number | null;
  retryCount: number;
  requestBytes: number | null;
  responseBytes: number | null;
  inputTokens: number | null;
  outputTokens: number | null;
  totalTokens: number | null;
  estimatedCostUsd: string | null;
  costStatus: string;
  cacheHit: boolean;
  streamed: boolean;
  idempotencyKey: string | null;
  mcpToolName: string | null;
  mcpClientName: string | null;
  createdAt: string;
  completedAt: string | null;
  attempts: Array<{
    attemptNumber: number;
    outcome: string;
    httpStatus: number | null;
    errorClass: string | null;
    errorDetail: string | null;
    failoverReason: string | null;
    latencyMs: number | null;
    startedAt: string;
    providerAccount: string | null;
  }>;
}

export function RequestsPage() {
  const [status, setStatus] = useState('');
  const [channel, setChannel] = useState('');
  const [operationKey, setOperationKey] = useState('');
  const [selected, setSelected] = useState<string | null>(null);

  const query = new URLSearchParams({ pageSize: '50' });
  if (status) query.set('status', status);
  if (channel) query.set('channel', channel);
  if (operationKey) query.set('operationKey', operationKey);

  const { data, loading, reload } = useResource<{ items: RequestRow[]; total: number }>(
    `/api/admin/requests?${query.toString()}`,
  );
  const detail = useResource<RequestDetail>(selected ? `/api/admin/requests/${selected}` : null);

  return (
    <>
      <PageHeader
        title="Dziennik żądań"
        description="Metadane, oś czasu prób i znormalizowane błędy. Treści żądań i odpowiedzi nie są przechowywane."
        actions={<Button onClick={reload}>Odśwież</Button>}
      />

      <Card tight>
        <div className="ah-card-body">
          <div className="ah-toolbar">
            <Field label="Status">
              {(id) => (
                <Select id={id} value={status} onChange={(event) => setStatus(event.target.value)}>
                  <option value="">Wszystkie</option>
                  <option value="SUCCESS">SUCCESS</option>
                  <option value="ERROR">ERROR</option>
                  <option value="PENDING">PENDING</option>
                  <option value="CANCELLED">CANCELLED</option>
                </Select>
              )}
            </Field>
            <Field label="Kanał">
              {(id) => (
                <Select
                  id={id}
                  value={channel}
                  onChange={(event) => setChannel(event.target.value)}
                >
                  <option value="">Wszystkie</option>
                  <option value="REST">REST</option>
                  <option value="MCP_DIRECT">MCP_DIRECT</option>
                  <option value="MCP_CATALOG">MCP_CATALOG</option>
                  <option value="ADMIN_CONSOLE">ADMIN_CONSOLE</option>
                </Select>
              )}
            </Field>
            <Field label="Operacja">
              {(id) => (
                <TextInput
                  id={id}
                  value={operationKey}
                  placeholder="np. llm.chat"
                  onChange={(event) => setOperationKey(event.target.value)}
                />
              )}
            </Field>
            <div className="spacer" />
            <span style={{ color: 'var(--ah-text-muted)', fontSize: 12 }}>
              {data ? `${formatNumber(data.total)} żądań` : ''}
            </span>
          </div>
        </div>

        {loading ? (
          <div className="ah-card-body">
            <Skeleton rows={6} />
          </div>
        ) : (
          <DataTable
            rows={data?.items ?? []}
            rowKey={(row) => row.id}
            empty={
              <EmptyState
                title="Brak żądań"
                description="Żaden ruch nie pasuje do wybranych filtrów."
              />
            }
            columns={[
              { key: 'time', header: 'Czas', render: (row) => formatDateTime(row.createdAt) },
              {
                key: 'status',
                header: 'Status',
                render: (row) => (
                  <>
                    <Badge tone={toneForRequestStatus(row.status)}>{row.status}</Badge>
                    {row.errorCode && (
                      <div style={{ marginTop: 2 }}>
                        <span className="ah-mono" style={{ fontSize: 11 }}>
                          {row.errorCode}
                        </span>
                      </div>
                    )}
                  </>
                ),
              },
              {
                key: 'operation',
                header: 'Operacja',
                render: (row) => (
                  <>
                    <span className="ah-mono">{row.operationKey ?? '—'}</span>
                    {row.operationVersion && (
                      <span style={{ color: 'var(--ah-text-subtle)' }}>
                        @{row.operationVersion}
                      </span>
                    )}
                    {row.modelAlias && (
                      <div style={{ fontSize: 11, color: 'var(--ah-text-muted)' }}>
                        alias: {row.modelAlias}
                      </div>
                    )}
                  </>
                ),
              },
              {
                key: 'caller',
                header: 'Wywołujący',
                render: (row) => (
                  <>
                    <Badge tone="info">{row.channel}</Badge>
                    <div style={{ fontSize: 12, marginTop: 2 }}>
                      {row.clientApp ?? row.developerToken ?? '—'}
                    </div>
                    {row.mcpToolName && (
                      <div
                        style={{ fontSize: 11, color: 'var(--ah-text-subtle)' }}
                        className="ah-mono"
                      >
                        {row.mcpToolName}
                      </div>
                    )}
                  </>
                ),
              },
              {
                key: 'latency',
                header: 'Czas',
                align: 'right',
                render: (row) => formatMs(row.latencyMs),
              },
              {
                key: 'attempts',
                header: 'Próby',
                align: 'right',
                render: (row) => (
                  <>
                    {row.attemptCount}
                    {row.retryCount > 0 && <Badge tone="warn">+{row.retryCount}</Badge>}
                  </>
                ),
              },
              {
                key: 'cost',
                header: 'Koszt',
                align: 'right',
                render: (row) => (
                  <>
                    {formatCost(row.estimatedCostUsd, row.costStatus)}
                    {row.totalTokens ? (
                      <div style={{ fontSize: 11, color: 'var(--ah-text-subtle)' }}>
                        {formatNumber(row.totalTokens)} tok.
                      </div>
                    ) : null}
                  </>
                ),
              },
              {
                key: 'actions',
                header: '',
                align: 'right',
                render: (row) => (
                  <Button size="sm" onClick={() => setSelected(row.id)}>
                    Szczegóły
                  </Button>
                ),
              },
            ]}
          />
        )}
      </Card>

      <Modal
        open={selected !== null}
        onOpenChange={(open) => !open && setSelected(null)}
        title="Szczegóły żądania"
        description={selected ?? ''}
      >
        {detail.loading && <Skeleton rows={5} />}
        {detail.data && (
          <>
            <KeyValue
              items={[
                [
                  'Status',
                  <Badge tone={toneForRequestStatus(detail.data.status)}>
                    {detail.data.status}
                  </Badge>,
                ],
                ['Kod błędu', detail.data.errorCode ?? '—'],
                [
                  'Operacja',
                  `${detail.data.operationKey ?? '—'}@${detail.data.operationVersion ?? '—'}`,
                ],
                ['Wersja katalogu', detail.data.catalogVersion ?? '—'],
                ['Środowisko', detail.data.environment],
                ['Kanał', detail.data.accessChannel],
                ['Tożsamość', detail.data.identityClass],
                ['Klient MCP', detail.data.mcpClientName ?? '—'],
                ['Czas całkowity', formatMs(detail.data.latencyMs)],
                ['Czas do pierwszego bajtu', formatMs(detail.data.ttfbMs)],
                ['Strumieniowanie', detail.data.streamed ? 'tak' : 'nie'],
                ['Cache', detail.data.cacheHit ? 'trafienie' : 'pominięty'],
                ['Klucz idempotencji', detail.data.idempotencyKey ?? '—'],
                [
                  'Tokeny',
                  detail.data.totalTokens
                    ? `${formatNumber(detail.data.inputTokens)} we / ${formatNumber(detail.data.outputTokens)} wy`
                    : '—',
                ],
                ['Koszt', formatCost(detail.data.estimatedCostUsd, detail.data.costStatus)],
              ]}
            />

            <h3 style={{ fontSize: 13, margin: '18px 0 8px' }}>Oś czasu prób</h3>
            <ul className="ah-timeline">
              {detail.data.attempts.map((attempt) => (
                <li key={attempt.attemptNumber}>
                  <span className={`step ${attempt.outcome === 'SUCCESS' ? 'ok' : 'danger'}`}>
                    {attempt.attemptNumber}
                  </span>
                  <div>
                    <div
                      style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}
                    >
                      <Badge tone={attempt.outcome === 'SUCCESS' ? 'ok' : 'danger'}>
                        {attempt.outcome}
                      </Badge>
                      {attempt.httpStatus && (
                        <span className="ah-mono">HTTP {attempt.httpStatus}</span>
                      )}
                      <span style={{ color: 'var(--ah-text-muted)' }}>
                        {formatMs(attempt.latencyMs)}
                      </span>
                      {attempt.providerAccount && (
                        <span className="ah-mono" style={{ color: 'var(--ah-text-muted)' }}>
                          {attempt.providerAccount}
                        </span>
                      )}
                    </div>
                    {attempt.failoverReason && (
                      <div style={{ fontSize: 12, color: 'var(--ah-text-muted)' }}>
                        Przełączenie: {attempt.failoverReason}
                      </div>
                    )}
                    {attempt.errorDetail && (
                      <pre className="ah-code" style={{ marginTop: 6, maxHeight: 120 }}>
                        {attempt.errorDetail}
                      </pre>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          </>
        )}
      </Modal>
    </>
  );
}
